import { type FC, useContext } from 'react';
import { subMonths } from 'date-fns';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { groupBy } from 'lodash';
import { CheckCircle } from 'lucide-react';

import { useMe } from '@/hooks/use-me';
import { plural } from '@/utils/format/label';
import {
  ConditionViolationColors,
  ConditionViolationLabel,
  ConditionViolationType,
} from '@/warranty/domain/entities/condition-violation-status';
import type { ConditionsViolationTimeseriesData } from '@/warranty/domain/entities/condition-violation-timeseries';
import { IWarrantyRepository } from '@/warranty/domain/repositories/warranty.repository';

import { WarrantyDetailsContext } from '../../pages/warranty-details';
import { GetConditionViolationTimeseriesData } from '../../queries/get-condition-violation-timeseries-data';
import { getBaseChartOptions } from '../common-chart-config';
import { PlotSection } from '../plot-section';
import { ChartSkeleton } from '../skeletons/ChartSkeleton';

import { IntroBox } from './intro-box';
import { ConditionViolationsTable } from './violations-table';

function fillMissingDates(
  data: ConditionsViolationTimeseriesData[],
  dateRange: { start: Date; end: Date },
): ConditionsViolationTimeseriesData[] {
  const allDates: Date[] = [];
  const current = new Date(dateRange.start);
  while (current <= dateRange.end) {
    allDates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return data.map((series) => {
    const dateMap = new Map<number, number>();
    series.timeseries.forEach(({ timestamp, count }) => {
      dateMap.set(new Date(timestamp).setHours(0, 0, 0, 0), count);
    });

    const filledTimeseries = allDates.map((date) => ({
      timestamp: date,
      count: dateMap.get(date.setHours(0, 0, 0, 0)) ?? 0,
    }));

    return {
      ...series,
      timeseries: filledTimeseries,
    };
  });
}

type HighchartsSeries = {
  name: string;
  data: [number, number][];
};
function aggregateByDescription(
  data: ConditionsViolationTimeseriesData[],
): HighchartsSeries[] {
  if (!data || data.length === 0) return [];

  const allTimestamps = data.flatMap((entry) =>
    entry.timeseries.map((point) => point.timestamp),
  );

  const minTimestamp = new Date(
    Math.min(...allTimestamps.map((d) => new Date(d).getTime())),
  );
  const maxTimestamp = new Date(
    Math.max(...allTimestamps.map((d) => new Date(d).getTime())),
  );

  const allDates: Date[] = [];
  const current = new Date(minTimestamp);
  current.setHours(0, 0, 0, 0);
  while (current <= maxTimestamp) {
    allDates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  const grouped = groupBy(data, (d) => d.description);

  return Object.entries(grouped).map(([description, entries]) => {
    const desc = description as ConditionViolationType;

    const dateCountMap = new Map<number, number>();
    for (const entry of entries) {
      for (const point of entry.timeseries) {
        const day = new Date(point.timestamp).setHours(0, 0, 0, 0);
        dateCountMap.set(day, (dateCountMap.get(day) || 0) + point.count);
      }
    }

    const filledTimeseries = allDates.map((date) => ({
      timestamp: date,
      count: dateCountMap.get(date.setHours(0, 0, 0, 0)) ?? 0,
    }));

    return {
      name: ConditionViolationLabel[desc],
      color: ConditionViolationColors[desc], // 🎨 use base color
      data: filledTimeseries.map(({ timestamp, count }) => [
        timestamp.getTime(),
        count,
      ]),
    };
  });
}

export const ZeroPenaltyState: FC = () => {
  return (
    <div className="flex h-full flex-col items-center justify-center rounded-xl border border-green-100 bg-green-50 p-6 text-center text-green-800 shadow-inner">
      <CheckCircle className="mb-3 size-10 text-green-600" />
      <div className="text-lg font-semibold">
        No condition violations detected
      </div>
      <div className="text-sm text-gray-600">
        All systems are operating within warranty conditions.
      </div>
    </div>
  );
};

export type ConditionViolationTimeseriesChartProps = {
  filters: IWarrantyRepository.Filters;
  // Add any props if needed in the future
};
export const ConditionViolationTimeseriesChart: FC<
  ConditionViolationTimeseriesChartProps
> = ({ filters }) => {
  const me = useMe();
  const asset = useContext(WarrantyDetailsContext);

  // get warranty result from dummy repository
  const conditionsViolations = GetConditionViolationTimeseriesData.useQuery({
    filters,
    assetFullFrontendId: asset!.fullFrontendId,
    assetId: asset!.id,
    customerId: me.config.id,
  });

  if (conditionsViolations.isLoading) return <ChartSkeleton />;
  if (conditionsViolations.isError)
    return <div>Error loading Warranty overview</div>;

  // check if conditionsViolations is undefined or empty
  if (!conditionsViolations.data || conditionsViolations.data.length === 0) {
    return <ZeroPenaltyState />;
  }

  const today = new Date();
  const oneYearAgo = subMonths(today, 1);

  const filledData = fillMissingDates(conditionsViolations.data, {
    start: oneYearAgo,
    end: today,
  });

  const series = aggregateByDescription(filledData);

  // Build your base config & override or extend as needed
  const baseConfig = getBaseChartOptions({
    title: `Condition Violations Over Time`,
    xAxisTitle: 'Time',
    yAxisTitle: `Count of ${plural(me.config.unit.labels.name).toLowerCase()}`,
    yAxisMax: null, // Maximum value remains unbounded
  });

  const options = {
    ...baseConfig,
    chart: {
      type: 'column',
    },

    plotOptions: {
      column: {
        stacking: null,
        softThreshold: false,
      },
    },
    series,
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export type ConditionViolationSectionProps = {
  filters: IWarrantyRepository.Filters;
};
export function ConditionViolationSection({
  filters,
}: ConditionViolationSectionProps) {
  return (
    <PlotSection sectionName="Condition Violations">
      <IntroBox />
      <div className="flex flex-row gap-x-10">
        <div className="h-96 w-2/5 flex-1">
          <ConditionViolationTimeseriesChart filters={filters} />
        </div>
        <div className="h-96 w-2/5 flex-1">
          <ConditionViolationsTable filters={filters} />
        </div>
      </div>
    </PlotSection>
  );
}
